import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import { getPriceList } from "../store/service";
import "./ProductsList.css";
import "./PriceListPrint.css";
import { getStoreLogoPath } from "../utils";

const formatPriceINR = (value) => {
  if (value == null || value === "") return "-";
  const number = Number(value);
  if (Number.isNaN(number)) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(number);
};

const formatSizeMm = ({ height, width, depth }) => {
  const h = height ?? "-";
  const w = width ?? "-";
  const d = depth ?? "-";
  return `${h} x ${w} x ${d}`;
};

const pickFirstTwoImages = (images) => {
  if (!images) return [];

  if (Array.isArray(images)) return images.filter(Boolean).slice(0, 2);

  const buckets = [];
  if (Array.isArray(images.r1)) buckets.push(...images.r1);

  Object.keys(images).forEach((k) => {
    const v = images[k];
    if (Array.isArray(v)) buckets.push(...v);
  });

  return buckets.filter(Boolean).slice(0, 2);
};

const PriceListProductHeader = ({ product }) => {
  const imageUrls = pickFirstTwoImages(product?.images);

  return (
    <div className="price-list-header">
      <div className="price-list-header-image">
        {imageUrls.map((src, i) => (
          <img
            key={`${src}-${i}`}
            src={src}
            alt={`${product.name} ${i === 0 ? "primary" : "secondary"}`}
          />
        ))}
      </div>
      <div className="price-list-header-text">
        <div className="price-list-header-text-name">{product.name}</div>
        {product?.code ? (
          <div className="price-list-header-text-code">{product.code}</div>
        ) : null}
        {Array.isArray(product?.prices) ? (
          <div className="price-list-header-text-count">
            {product.prices.length} products
          </div>
        ) : null}
      </div>
    </div>
  );
};

const RANGE_ORDER = [
  { key: "eco", label: "Eco" },
  { key: "prime", label: "Prime" },
  { key: "gold", label: "Gold" },
];

const buildPriceGroups = (prices = [], rangeFilter = null, productPage = null) => {
  if (!Array.isArray(prices)) return [];

  const ranges = rangeFilter
    ? RANGE_ORDER.filter(({ key }) => key === rangeFilter.toLowerCase())
    : RANGE_ORDER;

  return prices
    .map((p) => {
      const entries = ranges.flatMap(({ key, label }) => {
        const v = p?.[key];
        if (!v) return [];
        return [
          {
            range: label,
            item_code: v.item_code ?? "-",
            price: v.price,
          },
        ];
      });

      if (entries.length === 0) return null;
      const nonStandard = !!(p?.against_order || productPage?.against_order);
      return {
        sizeLabel: formatSizeMm(p ?? {}),
        nonStandard,
        entries,
      };
    })
    .filter(Boolean);
};

const renderPriceTable = (groups) => {
  if (!groups?.length) return null;

  const showNonStandardNote = groups.some((g) => g.nonStandard);

  return (
    <>
      <table className="price-list-table">
        <thead>
          <tr>
            <th>
              Size (mm)
              <div className="fw-normal" style={{ fontSize: "7pt" }}>
                H x W X D
              </div>
            </th>
            <th>Prduct Code</th>
            <th className="price-list-table-price">
              MRP
              <div className="fw-normal" style={{ fontSize: "7pt" }}>
                incl. GST
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group, groupIndex) =>
            group.entries.map((entry, entryIndex) => (
              <tr key={`${groupIndex}-${entryIndex}`}>
                {entryIndex === 0 && (
                  <td
                    className="price-list-table-size"
                    rowSpan={group.entries.length}
                  >
                    {group.sizeLabel}
                    {group.nonStandard ? (
                      <>
                        <sup style={{ fontSize: "8pt" }}>*</sup>{" "}
                      </>
                    ) : null}
                  </td>
                )}
                <td>{entry.item_code}</td>
                <td className="price-list-table-price">
                  {formatPriceINR(entry.price)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {showNonStandardNote ? (
        <div className="mt-1" style={{ fontSize: "7pt" }}>
          <sup>*</sup> Against order - 3 day delivery
        </div>
      ) : null}
    </>
  );
};

const GROUPS_PER_PAGE = 6;

const chunkGroups = (groups) => {
  const pages = [];
  for (let i = 0; i < groups.length; i += GROUPS_PER_PAGE) {
    pages.push(groups.slice(i, i + GROUPS_PER_PAGE));
  }
  return pages.length ? pages : [[]];
};

const renderBannerPage = (banner) => {
  const hasImage = !!banner?.image;
  return (
    <div className="price-list-banner-full">
      {hasImage ? (
        <img
          src={banner.image}
          alt="Banner"
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : null}
    </div>
  );
};

const renderPriceListPage = (product, pageNumber, groupsChunk, rangeLabel) => {
  return (
    <div className="price-list-page d-flex flex-column py-4">
      <div className="d-flex align-items-center justify-content-between">
        <img
          src={getStoreLogoPath()}
          alt="Zerobuild Logo"
          style={{ height: "8mm", objectFit: "contain" }}
        />
        {rangeLabel && (
          <span className="fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.05em" }}>
            {rangeLabel}
          </span>
        )}
      </div>

      <div className="price-list-content bg-white">
        <PriceListProductHeader product={product} />
        <div className="price-list-content-table-wrap">{renderPriceTable(groupsChunk)}</div>
      </div>

      <div className="d-flex justify-content-between fw-bold mt-auto">
        <span className="text-muted text-uppercase">zerobuild.in</span>
        <span className="text-muted">{pageNumber}</span>
      </div>
    </div>
  );
};

const PriceListPrint = () => {
  const [searchParams] = useSearchParams();
  const range = searchParams.get("range") || null;
  const rangeLabel = range
    ? (RANGE_ORDER.find(({ key }) => key === range.toLowerCase())?.label ?? range)
    : null;

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPriceList = async () => {
      try {
        const params = range ? { range } : {};
        const list = await getPriceList(params);
        setPages(Array.isArray(list) ? list : []);
      } catch (err) {
        setError(err?.error || err?.message || "Failed to fetch price list");
        setPages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPriceList();

    const style = document.createElement("style");
    style.innerHTML = `
      @media print {
        @page {
          size: 148mm 210mm;
          margin: 0;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [range]);

  if (loading) {
    return (
      <div className="catalog-products-list-container">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner color="primary" />
        </div>
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="price-list-print-root price-print-view d-flex flex-column">
        {(() => {
          const out = [];
          pages.forEach((p, index) => {
            if (p?.type === "banner") {
              out.push({ kind: "banner", banner: p, key: `banner-${index}` });
              return;
            }
            if (p?.type === "product") {
              const groups = buildPriceGroups(p.prices || [], range, p);
              const chunks = chunkGroups(groups);
              chunks.forEach((groupsChunk, chunkIndex) => {
                out.push({
                  kind: "product",
                  product: p,
                  groupsChunk,
                  key: `product-${index}-${chunkIndex}`,
                });
              });
            }
          });

          return out.map((item, pageIndex) => {
            const pageNumber = pageIndex + 1;
            if (item.kind === "banner") {
              return (
                <React.Fragment key={item.key}>
                  {renderBannerPage(item.banner)}
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={item.key}>
                {renderPriceListPage(
                  item.product,
                  pageNumber,
                  item.groupsChunk,
                  rangeLabel
                )}
              </React.Fragment>
            );
          });
        })()}
      </div>
    </>
  );
};

export default PriceListPrint;
